// Create / edit a project. A new project starts blank (stage names, one per
// line) or from a saved template, whose due dates count from the start date.
import { useState } from 'react'
import { Lock } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { DatePicker } from '../../components/ui/date-picker'
import { Field } from '../../components/ui/field'
import { Input } from '../../components/ui/input'
import { Select } from '../../components/ui/select'
import { fetchMe } from '../../lib/auth'
import { parsePlainDate, type PlainDate } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import {
  createProject,
  listAssignees,
  listFolders,
  listTemplates,
  PROJECT_STATUSES,
  StaffTaskApiError,
  staffTaskKeys,
  updateProject,
  type ProjectStatus,
  type StaffProject,
  type StaffProjectDetail,
} from './api'
import { AssigneePicker } from './AssigneePicker'
import { TEXTAREA_CLASS } from './TaskFormDialog'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  project?: StaffProject
  /** Open with the first template picked ("From template"). */
  fromTemplate?: boolean
  /** Pre-picked folder (a folder is open in the sidebar). */
  initialFolderId?: string
  onSaved?: (detail: StaffProjectDetail) => void
}

export function ProjectFormDialog({
  open,
  onOpenChange,
  project,
  fromTemplate,
  initialFolderId,
  onSaved,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <ProjectForm
          project={project}
          fromTemplate={fromTemplate}
          initialFolderId={initialFolderId}
          onClose={() => onOpenChange(false)}
          onSaved={onSaved}
        />
      )}
    </Dialog>
  )
}

function ProjectForm({
  project,
  fromTemplate,
  initialFolderId,
  onClose,
  onSaved,
}: {
  project?: StaffProject
  fromTemplate?: boolean
  initialFolderId?: string
  onClose: () => void
  onSaved?: (detail: StaffProjectDetail) => void
}) {
  const t = useT(staffTasksI18n)
  const p = t.projects
  const queryClient = useQueryClient()
  const [name, setName] = useState(project?.name ?? '')
  const [description, setDescription] = useState(project?.description ?? '')
  const [owner, setOwner] = useState(project?.owner?.user_id ?? '')
  const [status, setStatus] = useState<ProjectStatus>(project?.status ?? 'on_track')
  const [start, setStart] = useState<PlainDate | null>(
    project?.start_date ? parsePlainDate(project.start_date) : null,
  )
  const [target, setTarget] = useState<PlainDate | null>(
    project?.target_date ? parsePlainDate(project.target_date) : null,
  )
  const [stages, setStages] = useState('')
  const [folderId, setFolderId] = useState(project?.folder_id ?? initialFolderId ?? '')
  const folders = useQuery({ queryKey: staffTaskKeys.folders, queryFn: listFolders })
  // null = not chosen yet ("From template" then defaults to the first one).
  const [templateChoice, setTemplate] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [isPrivate, setPrivate] = useState(project?.is_private ?? false)
  const [members, setMembers] = useState<string[]>(
    (project?.members ?? []).map((person) => person.user_id),
  )
  const me = useQuery({ queryKey: ['auth', 'me'], queryFn: fetchMe, staleTime: 5 * 60 * 1000 })
  const meId = me.data?.user.id
  // Privacy and members: chosen when creating, afterwards only by the creator.
  const canManage = !project || !!project.can_manage_members

  const assignees = useQuery({ queryKey: staffTaskKeys.assignees, queryFn: listAssignees })
  const templates = useQuery({
    queryKey: staffTaskKeys.templates,
    queryFn: listTemplates,
    enabled: !project,
  })
  const templateId =
    templateChoice ?? (fromTemplate ? (templates.data?.items[0]?.id ?? '') : '')
  const template = templates.data?.items.find((item) => item.id === templateId)

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        owner_user_id: owner || null,
        status,
        start_date: start,
        target_date: target,
        folder_id: folderId || null,
      }
      const privacy = canManage
        ? {
            is_private: isPrivate,
            member_user_ids: isPrivate ? members.filter((id) => id !== meId) : [],
          }
        : {}
      if (project) return updateProject(project.id, { ...body, ...privacy })
      return createProject({
        ...body,
        ...privacy,
        template_id: templateId || null,
        stages: templateId
          ? []
          : stages
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean),
      })
    },
    onSuccess: (detail) => {
      toast.success(project ? p.toasts.saved : p.toasts.created)
      void queryClient.invalidateQueries({ queryKey: staffTaskKeys.all })
      onSaved?.(detail)
      onClose()
    },
    onError: (err) => {
      const code = err instanceof StaffTaskApiError ? err.code : undefined
      toast.error(
        code === 'assignee_not_in_team'
          ? t.toasts.notInTeam
          : code === 'staff_project_member_has_tasks'
            ? t.privacy.memberHasTasks
            : t.toasts.error,
      )
    },
  })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!name.trim()) {
      setNameError(p.form.nameRequired)
      return
    }
    save.mutate()
  }

  return (
    <DialogContent>
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="staff-project-form">
        <DialogHeader>
          <DialogTitle>{project ? p.form.editTitle : p.form.createTitle}</DialogTitle>
        </DialogHeader>

        {!project && (templates.data?.items.length ?? 0) > 0 && (
          <Field
            id="staff-project-template"
            label={p.form.templateLabel}
            helpText={template ? p.form.templateHelp(template.stage_count, template.task_count) : undefined}
          >
            <Select value={templateId} onChange={(e) => setTemplate(e.target.value)}>
              <option value="">{p.form.noTemplate}</option>
              {(templates.data?.items ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field id="staff-project-name" label={p.form.nameLabel} required error={nameError}>
          <Input
            autoFocus
            maxLength={200}
            value={name}
            placeholder={p.form.namePlaceholder}
            onChange={(e) => {
              setName(e.target.value)
              if (nameError) setNameError(null)
            }}
          />
        </Field>

        <Field id="staff-project-description" label={p.form.descriptionLabel}>
          <textarea
            rows={3}
            maxLength={10000}
            className={TEXTAREA_CLASS}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="staff-project-owner" label={p.form.ownerLabel}>
            <Select value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">{p.noOwner}</option>
              {(assignees.data?.items ?? []).map((person) => (
                <option key={person.user_id} value={person.user_id}>
                  {person.display_name}
                </option>
              ))}
            </Select>
          </Field>
          <Field id="staff-project-status" label={p.form.statusLabel}>
            <Select value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus)}>
              {PROJECT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {p.status[value]}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {(folders.data?.items.length ?? 0) > 0 && (
          <Field id="staff-project-folder" label={t.folderField.label}>
            <Select value={folderId} onChange={(e) => setFolderId(e.target.value)}>
              <option value="">{t.folderField.none}</option>
              {(folders.data?.items ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div
          className="flex flex-col gap-3 rounded-md border border-[var(--color-border)] p-3"
          data-testid="staff-project-privacy"
        >
          <label className="flex items-start gap-2 text-sm text-[var(--color-text)]">
            <input
              type="checkbox"
              checked={isPrivate}
              disabled={!canManage}
              onChange={(e) => setPrivate(e.target.checked)}
              className="mt-0.5 h-4 w-4"
              aria-describedby="staff-project-private-note"
            />
            <span>
              <span className="inline-flex items-center gap-1 font-medium">
                <Lock className="h-3.5 w-3.5" aria-hidden />
                {t.privacy.label}
              </span>
              <span
                id="staff-project-private-note"
                className="block text-xs text-[var(--color-text-muted)]"
              >
                {canManage ? t.privacy.help : t.privacy.onlyCreator}
              </span>
            </span>
          </label>
          {isPrivate && (
            <Field
              id="staff-project-members"
              label={t.privacy.members}
              helpText={t.privacy.membersHelp}
            >
              <AssigneePicker
                id="staff-project-members"
                people={(assignees.data?.items ?? []).filter((person) => person.user_id !== meId)}
                value={members.filter((id) => id !== meId)}
                onChange={setMembers}
                disabled={!canManage}
                addLabel={t.privacy.addMember}
              />
            </Field>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <DatePicker label={p.form.startLabel} value={start} onChange={setStart} />
          <DatePicker label={p.form.targetLabel} value={target} onChange={setTarget} />
        </div>

        {!project && !templateId && (
          <Field id="staff-project-stages" label={p.form.stagesLabel} helpText={p.form.stagesHelp}>
            <textarea
              rows={4}
              className={TEXTAREA_CLASS}
              value={stages}
              onChange={(e) => setStages(e.target.value)}
            />
          </Field>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t.form.cancel}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {project ? t.form.save : p.newProject}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
